import {
  Zap,
  Shield,
  Clock,
  MessageCircle,
  CheckCircle2,
} from "lucide-react";
import { CONTACT_CONFIG } from "../config/contact";
import Carousel from "../components/Carousel";
import GridTexture from "../components/GridTexture";

const filmContext = require.context("../assets/cover/film", false, /\.(png|jpe?g|svg|webp)$/);
const filmImages = filmContext.keys().map((key) => filmContext(key));

const sportsContext = require.context("../assets/cover/sports", false, /\.(png|jpe?g|svg|webp)$/);
const sportsImages = sportsContext.keys().map((key) => sportsContext(key));

const channelContext = require.context("../assets/cover/chaine", false, /\.(png|jpe?g|svg|webp)$/);
const channelImages = channelContext.keys().map((key) => channelContext(key));

function scrollToPricing() {
  const element = document.getElementById("pricing");
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
}

function PosterTile({ src, className = "" }) {
  return (
    <div
      className={`relative aspect-[2/3] rounded-xl sm:rounded-2xl overflow-hidden shadow-lg group ${className}`}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
    </div>
  );
}

function LogoTile({ src, className = "" }) {
  return (
    <div
      className={`relative aspect-[5/2] rounded-xl sm:rounded-2xl overflow-hidden shadow-lg bg-white flex items-center justify-center p-1.5 sm:p-2 ${className}`}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="max-w-full max-h-full object-contain"
      />
    </div>
  );
}

export default function Home() {
  const { whatsappUrl: WHATSAPP_URL } = CONTACT_CONFIG;
  const sportsColumn = sportsImages.slice(0, 8);
  const filmsColumn = filmImages.slice(0, 8);
  const channelsColumn = channelImages.slice(0, 8);

  return (
    <>
      <section className="home-section relative overflow-hidden bg-black">
        {/* Ambient glow / cinematic atmosphere — cyan, purple & pink light behind the content */}
        <div className="absolute -top-24 -left-24 w-72 h-72 sm:w-96 sm:h-96 bg-[#00d4ff]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-80 sm:w-[28rem] sm:h-[28rem] bg-[#8b5cf6]/[0.12] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-24 w-72 h-72 sm:w-96 sm:h-96 bg-[#ec4899]/15 rounded-full blur-3xl pointer-events-none" />
        <GridTexture />

        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto pt-8 sm:pt-12 lg:pt-16 pb-10 sm:pb-14 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* Left content */}
            <div className="lg:col-span-7 space-y-5 lg:space-y-7">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-3.5 py-1.5 text-sm sm:text-base font-bold uppercase tracking-[0.16em] text-white">
                  <Zap className="w-4 h-4 text-[#00d4ff]" />
                  Directe Activering
                </span>
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-3.5 py-1.5 text-sm sm:text-base font-bold uppercase tracking-[0.16em] text-white">
                  <Shield className="w-4 h-4 text-[#8b5cf6]" />
                  15 Dagen Garantie
                </span>
              </div>

              <div className="space-y-3 lg:space-y-4">
                <h1 className="text-[clamp(2.1rem,1.6rem+2.5vw,4rem)] font-black text-white leading-[1.08] tracking-tight main-title">
                  De beste{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-text">
                    IPTV-ervaring
                  </span>{" "}
                  van Nederland &amp; België
                </h1>

                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-6 sm:w-9 sm:h-7 rounded shadow-sm border border-white/20" viewBox="0 0 640 480" role="img" aria-label="Nederland">
                    <path fill="#AE1C28" d="M0 0h640v160H0z"/>
                    <path fill="#FFF" d="M0 160h640v160H0z"/>
                    <path fill="#21468B" d="M0 320h640v160H0z"/>
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-6 sm:w-9 sm:h-7 rounded shadow-sm border border-white/20" viewBox="0 0 640 480" role="img" aria-label="België">
                    <path fill="#000" d="M0 0h213.3v480H0z"/>
                    <path fill="#FFD90C" d="M213.3 0h213.4v480H213.3z"/>
                    <path fill="#F31830" d="M426.7 0H640v480H426.7z"/>
                  </svg>
                  <h2 className="text-base sm:text-lg lg:text-xl font-bold text-blue-100/90">
                    Premium entertainment voor al jouw apparaten
                  </h2>
                </div>

                <p className="text-sm sm:text-base text-blue-100/70 max-w-xl leading-relaxed">
                  79.000+ live zenders en 249.000+ films en series in HD/4K, zonder buffering —
                  op de officiële TiviOne-server voor Nederland en België.
                </p>
              </div>

              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-2.5 sm:gap-4 max-w-md">
                <div className="bg-white/[0.07] backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/10">
                  <div className="text-lg sm:text-2xl font-black text-[#00d4ff]">79K+</div>
                  <div className="text-[11px] sm:text-xs text-blue-100/70 font-medium">Zenders</div>
                </div>
                <div className="bg-white/[0.07] backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/10">
                  <div className="text-lg sm:text-2xl font-black text-[#00d4ff]">249K+</div>
                  <div className="text-[11px] sm:text-xs text-blue-100/70 font-medium">VOD</div>
                </div>
                <div className="bg-white/[0.07] backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/10">
                  <div className="text-lg sm:text-2xl font-black text-[#00d4ff]">4K</div>
                  <div className="text-[11px] sm:text-xs text-blue-100/70 font-medium">Kwaliteit</div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  onClick={scrollToPricing}
                  className="bg-gradient-button hover:brightness-110 text-white px-6 py-3.5 rounded-xl font-black text-sm sm:text-base shadow-glow-purple hover:shadow-2xl transition-all duration-300 hover:scale-[1.03] flex items-center justify-center gap-2 min-h-[48px]"
                >
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  Bekijk Onze Pakketten
                </button>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/15 backdrop-blur-sm text-white border border-white/20 px-6 py-3.5 rounded-xl font-bold text-sm sm:text-base transition-all min-h-[48px] flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Stel een Vraag
                </a>
              </div>

              {/* Trust chips */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
                {["Officiële TiviOne-server", "Direct actief", "15 dagen garantie"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-blue-100/80 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-[#00d4ff] flex-shrink-0" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right visual: vertical poster carousels + floating trust badges */}
            <div className="lg:col-span-5">
              <div className="relative max-w-sm mx-auto lg:max-w-none">
                <div className="grid grid-cols-3 gap-3 sm:gap-4 h-[380px] sm:h-[460px] lg:h-[500px]">
                  {/* Column 1: Sport */}
                  <Carousel
                    axis="y"
                    direction="normal"
                    speed={0.35}
                    gapClassName="gap-3 sm:gap-4"
                    items={sportsColumn}
                    keyExtractor={(src) => src}
                    renderItem={(src) => <PosterTile src={src} />}
                  />
                  {/* Column 2: Films & series */}
                  <Carousel
                    axis="y"
                    direction="reverse"
                    speed={0.35}
                    gapClassName="gap-3 sm:gap-4"
                    items={filmsColumn}
                    keyExtractor={(src) => src}
                    renderItem={(src) => <PosterTile src={src} />}
                  />
                  {/* Column 3: Channels */}
                  <Carousel
                    axis="y"
                    direction="normal"
                    speed={0.35}
                    gapClassName="gap-3 sm:gap-4"
                    items={channelsColumn}
                    keyExtractor={(src) => src}
                    renderItem={(src) => <LogoTile src={src} />}
                  />
                </div>

                {/* Floating badge: guarantee */}
                <div className="absolute -left-3 sm:-left-6 top-2 sm:top-6 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-white/50 max-w-[130px] sm:max-w-[150px]">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-[#8b5cf6] mb-1" />
                  <div className="text-[11px] sm:text-xs font-bold text-gray-900 leading-tight">
                    15 Dagen Restitutie
                  </div>
                </div>

                {/* Floating badge: speed */}
                <div className="absolute -right-3 sm:-right-6 bottom-2 sm:bottom-6 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-white/50 max-w-[130px] sm:max-w-[150px]">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#00a8cc] mb-1" />
                  <div className="text-[11px] sm:text-xs font-bold text-gray-900 leading-tight">
                    0.5s Laadtijd
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
